import type { default as _jQuery } from "jquery";
import { type Ref, watch, type ComputedRef } from 'vue';
import { type Nil, isString } from '@mcwd/typescript-type-guards';
import { AnyMarketoInputName } from "../../types/definitions/inputs/marketo-input-names-and-values.js";
import { useFormLogger } from "../use-form-logger.js";
import { MarketoAllInputMeta } from "../../types/definitions/inputs/marketo-input-meta.js";
import type { HtmlInputTypeNameToHTMLElement } from "../../../types-and-definitions/forms/input-element.js";
import { getRawInputElementValue, type RawInputElementValue } from "./getRawInputElementValue.js";
import type { MarketoInputNameToHTMLElement } from "./MarketoInputNameToHTMLElement.js";
import { useMarketoFormInputElement } from "./useMarketoFormInputElement.js";

export function useMarketoInputProxy<
  TInputName extends AnyMarketoInputName,
  TValue extends RawInputElementValue<MarketoInputNameToHTMLElement<TInputName>>
> ({
  inputName,
  proxyInputValue,
  callerLabel
}: {
  inputName: TInputName;
    proxyInputValue: Extract<Ref<TValue>, Ref<RawInputElementValue<MarketoInputNameToHTMLElement<TInputName>>>>,
  callerLabel?: string | undefined;
});

export function useMarketoInputProxy<
  TInputName extends AnyMarketoInputName,
  TValue extends string | string[] | number | boolean | Nil
> ({
  inputName,
  proxyInputValue,
  callerLabel,
  getValue
}: {
  inputName: TInputName;
  proxyInputValue: Ref<TValue>;
    getValue: (inputEl: MarketoInputNameToHTMLElement<TInputName>) => TValue;
  callerLabel?: string | undefined;
});

export function useMarketoInputProxy<
  TInputName extends AnyMarketoInputName,
  TValue extends string | string[] | number | boolean | Nil
>({
  inputName,
  proxyInputValue,
  getValue,
  callerLabel
}: {
  inputName: TInputName;
  proxyInputValue: Ref<TValue>;
  getValue?: (inputEl: MarketoInputNameToHTMLElement<TInputName>) => NoInfer<TValue>;
  callerLabel?: string | undefined;
}) {
  type TypeName = MarketoAllInputMeta[ TInputName ][ "type" ];
  type TInputElement = HtmlInputTypeNameToHTMLElement<TypeName>;
  type ValueGetterFn = (el: TInputElement) => TValue;
  type ThisReturnType = { inputElement: ComputedRef<TInputElement | Nil>, proxyInputValue: Ref<TValue> };

  const { logger } = useFormLogger({ callerLabel: "useMarketoFormInputProxy" });
  const definedGetValueFn: ValueGetterFn = getValue ?? (
    el => (getRawInputElementValue(el) as unknown as TValue)
  );

  try {
    callerLabel ??= "useMarketoInputProxy";
    const initialValue =
      isString(proxyInputValue.value) && proxyInputValue.value.trim() === ""
        ? null
        : proxyInputValue.value;
    const { inputElement, inputValue } = useMarketoFormInputElement({
      inputName,
      initialValue,
      callerLabel,
      getValue: definedGetValueFn
    });
    watch(
      () => proxyInputValue.value,
      (value) => {
        const val = isString(value) && value.trim() === "" ? null : value;
        inputValue.value = val;
      }
    );
    return { inputElement, proxyInputValue } as ThisReturnType;
  } catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}